// Components/Utilities/TimePicker.js
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';

const TimePicker = ({ 
  value, 
  onChange, 
  label, 
  error, 
  helperText, 
  disabled
}) => {
  const [open, setOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(value || dayjs());
  const [meridiem, setMeridiem] = useState(value ? value.format('A') : 'AM');
  const [selectingMinutes, setSelectingMinutes] = useState(false);
  const clockRef = useRef(null);

  const handleOpen = () => {
    if (!disabled) {
      setSelectedTime(value || dayjs());
      setMeridiem(value ? value.format('A') : 'AM');
      setSelectingMinutes(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOk = () => {
    onChange(selectedTime);
    setOpen(false);
  };

  const handleHourSelect = (hour) => {
    // Convert to 24-hour format based on AM/PM
    let newHour = hour;
    if (meridiem === 'PM' && hour !== 12) {
      newHour += 12;
    } else if (meridiem === 'AM' && hour === 12) {
      newHour = 0;
    }
    
    const newTime = selectedTime.hour(newHour);
    setSelectedTime(newTime);
    setSelectingMinutes(true);
  };

  const handleMinuteSelect = (minute) => {
    const newTime = selectedTime.minute(minute);
    setSelectedTime(newTime);
  };

  const handleClockClick = (event) => {
    if (!clockRef.current) return;

    const rect = clockRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Calculate angle from center (0° at top, increasing clockwise)
    const deltaX = clickX - centerX;
    const deltaY = clickY - centerY;
    let angle = Math.atan2(deltaX, -deltaY) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    if (selectingMinutes) {
      // Convert angle to minute (0-59)
      const minute = Math.round((angle / 360) * 60) % 60;
      handleMinuteSelect(minute);
    } else {
      // Convert angle to hour (1-12)
      const hour = Math.round((angle / 360) * 12) % 12 || 12;
      handleHourSelect(hour);
    }
  };

  const handleMeridiemChange = (event, newMeridiem) => {
    if (newMeridiem !== null) {
      setMeridiem(newMeridiem);
      // Update time with new AM/PM
      let currentHour = selectedTime.hour();
      let newHour = currentHour;
      
      if (newMeridiem === 'PM' && currentHour < 12) {
        newHour = currentHour + 12;
      } else if (newMeridiem === 'AM' && currentHour >= 12) {
        newHour = currentHour - 12;
      }
      
      const newTime = selectedTime.hour(newHour);
      setSelectedTime(newTime);
    }
  };

  const getDisplayHour = () => {
    let hour = selectedTime.hour();
    if (hour === 0) return 12;
    if (hour > 12) return hour - 12;
    return hour;
  };

  const getDisplayMinutes = () => {
    return selectedTime.minute().toString().padStart(2, '0');
  };

  const getClockValues = () => {
    if (selectingMinutes) {
      // Show all 60 minutes in circular format
      const minuteMarkers = [];
      for (let i = 0; i < 60; i += 5) {
        minuteMarkers.push({
          value: i,
          label: i.toString().padStart(2, '0'),
          angle: (i / 60) * 360
        });
      }
      return minuteMarkers;
    } else {
      // Hour markers (1-12)
      return [
        { value: 12, label: '12', angle: 0 },
        { value: 1, label: '1', angle: 30 },
        { value: 2, label: '2', angle: 60 },
        { value: 3, label: '3', angle: 90 },
        { value: 4, label: '4', angle: 120 },
        { value: 5, label: '5', angle: 150 },
        { value: 6, label: '6', angle: 180 },
        { value: 7, label: '7', angle: 210 },
        { value: 8, label: '8', angle: 240 },
        { value: 9, label: '9', angle: 270 },
        { value: 10, label: '10', angle: 300 },
        { value: 11, label: '11', angle: 330 }
      ];
    }
  };

  const getSelectedAngle = () => {
    if (selectingMinutes) {
      return (selectedTime.minute() / 60) * 360;
    } else {
      const hour = getDisplayHour();
      return (hour / 12) * 360;
    }
  };

  const getMarkerPosition = (angle, radius = 100) => {
    const rad = (angle - 90) * (Math.PI / 180); // -90° to start from top
    return {
      x: Math.cos(rad) * radius,
      y: Math.sin(rad) * radius
    };
  };

  const displayValue = value ? value.format('hh:mm A') : '';

  return (
    <FormControl fullWidth error={!!error}>
      <TextField
        label={label}
        value={displayValue}
        onClick={handleOpen}
        error={!!error}
        helperText={helperText}
        disabled={disabled}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpen} edge="end" disabled={disabled}>
                <AccessTimeIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            SELECT TIME
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center' }}>
          {/* Time Display */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {getDisplayHour()}:{getDisplayMinutes()}
            </Typography>
            <ToggleButtonGroup
              value={meridiem}
              exclusive
              onChange={handleMeridiemChange}
              sx={{ mt: 1 }}
            >
              <ToggleButton value="AM" sx={{ px: 3 }}>
                AM
              </ToggleButton>
              <ToggleButton value="PM" sx={{ px: 3 }}>
                PM
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Clock Face */}
          <Box 
            ref={clockRef}
            sx={{ 
              position: 'relative', 
              width: 280, 
              height: 280, 
              mx: 'auto', 
              mb: 3,
              cursor: 'pointer',
              touchAction: 'none'
            }}
            onClick={handleClockClick}
          >
            {/* Clock Circle */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: '50%',
                backgroundColor: 'background.paper'
              }}
            />
            
            {/* Clock Markers */}
            {getClockValues().map((marker) => {
              const position = getMarkerPosition(marker.angle, 100);
              const isSelected = selectingMinutes 
                ? selectedTime.minute() === marker.value
                : getDisplayHour() === marker.value;
              
              return (
                <Box
                  key={marker.value}
                  sx={{
                    position: 'absolute',
                    left: `calc(50% + ${position.x}px)`,
                    top: `calc(50% + ${position.y}px)`,
                    transform: 'translate(-50%, -50%)',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: isSelected ? 'primary.main' : 'transparent',
                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                    pointerEvents: 'none',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    fontSize: '0.875rem',
                    border: isSelected ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography variant="body2" fontWeight="inherit">
                    {marker.label}
                  </Typography>
                </Box>
              );
            })}

            {/* Selected Time Indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 4,
                height: 100,
                backgroundColor: 'primary.main',
                transform: `translate(-50%, -100%) rotate(${getSelectedAngle()}deg)`,
                transformOrigin: 'center bottom',
                borderRadius: '2px',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                }
              }}
            />

            {/* Center Dot */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                border: '2px solid white',
                boxShadow: '0 0 0 2px primary.main',
              }}
            />
          </Box>

          {/* Mode Navigation */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
            <Button
              variant={!selectingMinutes ? "contained" : "outlined"}
              onClick={() => setSelectingMinutes(false)}
              size="small"
            >
              Hour
            </Button>
            <Button
              variant={selectingMinutes ? "contained" : "outlined"}
              onClick={() => setSelectingMinutes(true)}
              size="small"
            >
              Minute
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Button onClick={handleClose} color="inherit">
            CANCEL
          </Button>
          <Button 
            onClick={handleOk} 
            variant="contained" 
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </FormControl>
  );
};

export default TimePicker;